import React from 'react';
import { useTranslation } from 'react-i18next';

import { PartDemand } from '../../services/partDemandTypes';
import { DataTable } from '../DataTable';
import { formatDate } from '../quoteToCash/format';

import { formatAverage, formatQty } from './format';
import { Meter, Stat, StatBand } from './Stats';

/** Stock now, the part's planning settings, and Epicor's time-phased supply and demand with the projected balance */
export const PlanningSection: React.FC<{ data: PartDemand }> = ({ data }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { planning, part } = data;
  const qty = (value: number) => formatQty(value, part.uom, lang);

  const below = planning.minStock > 0 && planning.onHand < planning.minStock;
  const { averageDays, receipts } = planning.leadTime;

  return (
    <>
      <StatBand boxed={false}>
        <Stat
          label={t('partDemand.planning.onHand')}
          value={qty(planning.onHand)}
          suffix={part.uom}
          detail={
            <>
              <p title={t('partDemand.planning.allocatedHelp')}>
                {t('partDemand.planning.availableLine', {
                  available: qty(planning.available),
                  allocated: qty(planning.allocated),
                })}
              </p>
              {below && (
                <p className="font-medium text-amber-800">
                  {t('partDemand.planning.belowMin', {
                    min: qty(planning.minStock),
                  })}
                </p>
              )}
            </>
          }
        >
          <Meter value={planning.onHand} minimum={planning.minStock} />
        </Stat>
        <Stat
          label={t('partDemand.planning.minStock')}
          value={qty(planning.minStock)}
          suffix={part.uom}
          detail={t('partDemand.planning.lotLine', {
            lot: qty(planning.minLotSize),
          })}
        />
        <Stat
          label={t('partDemand.planning.averageLeadTime')}
          value={averageDays === null ? '—' : formatAverage(averageDays, lang)}
          suffix={
            averageDays === null
              ? undefined
              : t('partDemand.planning.daysUnit', { count: averageDays })
          }
          title={t('partDemand.planning.averageLeadTimeHelp')}
          detail={
            averageDays === null
              ? t('partDemand.planning.noReceipts')
              : t('partDemand.planning.receiptsLine', {
                  count: receipts,
                  date: formatDate(data.history.startDate, lang, 'long'),
                })
          }
        />
      </StatBand>

      <h3 className="mt-6 pt-4 border-t border-gray-200 text-sm font-semibold text-gray-900 mb-2">
        {t('partDemand.planning.timePhased', {
          supply: qty(planning.openSupply),
          demand: qty(planning.openDemand),
        })}
      </h3>
      <DataTable
        data={planning.rows}
        keyExtractor={(_, i) => i}
        isEmpty={planning.rows.length === 0}
        emptyMessage={t('partDemand.planning.noTimePhased')}
        getRowClassName={(row) => (row.date < data.asOf ? 'bg-red-50' : '')}
        columns={[
          {
            key: 'date',
            header: t('partDemand.planning.date'),
            render: (value) => formatDate(value as string, lang, 'long'),
          },
          {
            key: 'kind',
            header: t('partDemand.planning.type'),
            render: (value) => t(`partDemand.planning.kind.${value as string}`),
          },
          {
            key: 'supply',
            header: t('partDemand.planning.supply'),
            align: 'right',
            render: (value) =>
              (value as number) > 0 ? qty(value as number) : '',
          },
          {
            key: 'demand',
            header: t('partDemand.planning.demand'),
            align: 'right',
            render: (value) =>
              (value as number) > 0 ? qty(value as number) : '',
          },
          {
            key: 'balance',
            header: t('partDemand.planning.balance'),
            align: 'right',
            render: (value) => (
              <span
                className={
                  (value as number) < planning.minStock
                    ? 'text-red-700 font-medium'
                    : ''
                }
              >
                {qty(value as number)}
              </span>
            ),
          },
          { key: 'reference', header: t('partDemand.planning.reference') },
        ]}
      />
      <p className="mt-2 text-xs text-gray-500">
        {t('partDemand.planning.balanceNote')}
      </p>
    </>
  );
};
